import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { getMedalImageUrl } from '../../utils/formatters';
import usePermissions from '../../hooks/usePermissions';
import ImageLightbox from '../common/ImageLightbox';
import LeagueAdminPanel from './LeagueAdminPanel';
import SeasonFixtures from './SeasonFixtures';
import SeasonPlayerStats from './SeasonPlayerStats';
import JoinSeason from './JoinSeason';
import RegionBadge from './RegionBadge';
import TeamLogo from './TeamLogo';
import LeagueHallOfFame from './LeagueHallOfFame';
import LeagueAllTimeStats from './LeagueAllTimeStats';
import './Leagues.css';

// Placement labels translated; keys resolved via t() at render (module-level
// constant can't call hooks). Shared with the leagues.* group.
const PLACEMENT_LABEL = { 1: 'leagues.placeGold', 2: 'leagues.placeSilver', 3: 'leagues.placeBronze' };
const TIER_CLASS = { 1: 'gold', 2: 'silver', 3: 'bronze' };

/**
 * Champion-medal showcase for a finished season: shows the AI-generated medal
 * for each top placement (gold/silver/bronze) with the winning participant's
 * name. Clicking a medal opens it in the shared image lightbox.
 *
 * @param {object} props - Component props.
 * @param {Array<object>} props.medals - Season medals from the API (placement-ordered).
 * @param {string} props.seasonName - The season's display name, for the lightbox title.
 */
function SeasonChampions({ medals, seasonName }) {
  const { t } = useTranslation();
  const [lightbox, setLightbox] = useState(null);
  if (!medals.length) return null;
  const placeLabel = (placement) => (PLACEMENT_LABEL[placement] ? t(PLACEMENT_LABEL[placement]) : `#${placement}`);
  return (
    <div className="league-champions">
      <h2 className="league-section-title">{t('leagues.champions')}</h2>
      <div className="league-champion-list">
        {medals.map((m) => {
          const tier = TIER_CLASS[m.placement] || '';
          return (
            <div key={m.id} className={`league-champion-item ${tier}`}>
              {m.image_filename && (
                <img
                  src={getMedalImageUrl(m.image_filename)}
                  alt={t('leagues.championMedalAlt', { place: placeLabel(m.placement) })}
                  className="league-champion-medal clickable"
                  onClick={() => setLightbox(m)}
                />
              )}
              <span className={`league-champion-rank ${tier || 'other'}`}>
                {placeLabel(m.placement)}
              </span>
              <span className="league-champion-name">
                {m.participant_name || '—'}
              </span>
            </div>
          );
        })}
      </div>
      {lightbox && (
        <ImageLightbox
          src={getMedalImageUrl(lightbox.image_filename)}
          title={t('tournament.medalLightboxTitle', { name: seasonName, place: placeLabel(lightbox.placement) })}
          subtitle={lightbox.participant_name || ''}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

/**
 * One standings table for a division: ranks participants best-first using the
 * order already provided by the API (points, then round differential, etc.).
 *
 * @param {object} props - Component props.
 * @param {object} props.division - Division object with a ``participants`` array.
 * @param {boolean} props.teamBased - Whether competitors are teams (vs players).
 * @param {Function} props.onRow - Called with a participant to open its detail.
 */
function DivisionStandings({ division, teamBased, onRow }) {
  const { t } = useTranslation();
  return (
    <div className="division-block">
      <h3 className="division-title">{division.name}</h3>
      <table className="standings-table">
        <thead>
          <tr>
            <th className="col-rank">#</th>
            <th className="col-name">{teamBased ? t('createMatch.teams') : t('leagues.competitor')}</th>
            <th className="col-num">{t('leagues.colPlayed')}</th>
            <th className="col-num">{t('leagues.colWins')}</th>
            <th className="col-num">{t('leagues.colDraws')}</th>
            <th className="col-num">{t('leagues.colLosses')}</th>
            <th className="col-num">+/−</th>
            <th className="col-num col-points">{t('leagues.colPoints')}</th>
          </tr>
        </thead>
        <tbody>
          {division.participants.map((p) => {
            const clickable = Boolean(p.team) || Boolean(p.steamid64);
            return (
              <tr
                key={p.id}
                className={`standings-row ${clickable ? 'is-clickable' : ''}`}
                onClick={clickable ? () => onRow(p) : undefined}
              >
                <td className="col-rank">{p.rank ?? '–'}</td>
                <td className="col-name">
                  {p.team && (
                    <TeamLogo logoUrl={p.team.logo_url} name={p.team.name} tag={p.team.tag} fallback />
                  )}
                  {p.team?.color && (
                    <span className="team-color-dot" style={{ '--team-color': p.team.color }} />
                  )}
                  {(p.team?.region || p.country) && (
                    <RegionBadge region={p.team ? p.team.region : p.country} className="standings-flag" />
                  )}
                  {p.display_name}
                  {p.provisional && (
                    <span className="standings-provisional" title={t('leagues.provisionalTitle')}>*</span>
                  )}
                </td>
                <td className="col-num">{p.played}</td>
                <td className="col-num">{p.wins}</td>
                <td className="col-num">{p.draws}</td>
                <td className="col-num">{p.losses}</td>
                <td className={`col-num ${p.round_diff >= 0 ? 'diff-pos' : 'diff-neg'}`}>
                  {p.round_diff > 0 ? `+${p.round_diff}` : p.round_diff}
                </td>
                <td className="col-num col-points">{p.points}</td>
              </tr>
            );
          })}
          {division.participants.length === 0 && (
            <tr>
              <td colSpan={8} className="standings-empty">{t('leagues.noParticipants')}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
 * League detail page: shows a league's standings for a selectable season. The
 * latest season is selected by default; a dropdown lets visitors browse past
 * seasons. Participant rows link through to the player profile when individual.
 */
export default function LeagueDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { canManageLeagues } = usePermissions();
  const [league, setLeague] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState(null);
  const [standings, setStandings] = useState(null);
  const [medals, setMedals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [standingsTick, setStandingsTick] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');

  // Load the league plus its season list, then default to the latest season.
  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      fetch(`${API_URL}/api/leagues/${id}`).then((r) => (r.ok ? r.json() : null)),
      fetch(`${API_URL}/api/leagues/${id}/seasons`).then((r) => (r.ok ? r.json() : [])),
    ])
      .then(([lg, seasonList]) => {
        if (!active) return;
        setLeague(lg);
        const list = Array.isArray(seasonList) ? seasonList : [];
        setSeasons(list);
        setSeasonId(list.length ? list[0].id : null);
      })
      .catch(() => active && setLeague(null))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [id]);

  // Load standings whenever the selected season (or a refresh tick) changes.
  useEffect(() => {
    if (!seasonId) {
      setStandings(null);
      return;
    }
    let active = true;
    fetch(`${API_URL}/api/leagues/seasons/${seasonId}/standings`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => active && setStandings(data))
      .catch(() => active && setStandings(null));
    return () => {
      active = false;
    };
  }, [seasonId, standingsTick]);

  // Load champion medals when the selected season changes. Medals only change
  // on season close, so this doesn't track standingsTick.
  useEffect(() => {
    if (!seasonId) {
      setMedals([]);
      return;
    }
    let active = true;
    fetch(`${API_URL}/api/leagues/seasons/${seasonId}/medals`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => active && setMedals(Array.isArray(data) ? data : []))
      .catch(() => active && setMedals([]));
    return () => {
      active = false;
    };
  }, [seasonId]);

  // Re-fetch the league itself (after an admin edits its settings).
  const refreshLeague = useCallback(() => {
    fetch(`${API_URL}/api/leagues/${id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((lg) => lg && setLeague(lg))
      .catch(() => {});
  }, [id]);

  // Re-fetch standings in place (after a registration / result / link).
  const refreshStandings = useCallback(() => setStandingsTick((t) => t + 1), []);

  // Re-fetch the season list (after create / activate / close) and optionally
  // jump to a specific season (e.g. the freshly created or generated one).
  const refreshSeasons = useCallback(
    async (selectId) => {
      const list = await fetch(`${API_URL}/api/leagues/${id}/seasons`)
        .then((r) => (r.ok ? r.json() : []))
        .catch(() => []);
      const arr = Array.isArray(list) ? list : [];
      setSeasons(arr);
      if (selectId) {
        setSeasonId(selectId);
      } else {
        // Keep the current season if it still exists, else fall back to the
        // latest one (e.g. after the selected season was deleted).
        setSeasonId((cur) => (arr.some((s) => s.id === cur) ? cur : (arr[0]?.id ?? null)));
      }
      setStandingsTick((t) => t + 1);
      return arr;
    },
    [id],
  );

  // A standings row opens the team page for team leagues, else the player profile.
  const openRow = useCallback((p) => {
    if (p.team) navigate(`/leagues/teams/${p.team.id}`);
    else if (p.steamid64) navigate(`/stats/players/${p.steamid64}`);
  }, [navigate]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loader" />
      </div>
    );
  }

  if (!league) {
    return <div className="league-empty">{t('leagues.notFound')}</div>;
  }

  const divisions = standings?.divisions || [];
  const selectedSeason = seasons.find((s) => s.id === seasonId) || null;
  // The admin tab is only reachable by league managers; everyone else is pinned
  // to the overview even if state somehow drifted.
  const tab = activeTab === 'admin' && canManageLeagues ? 'admin' : 'overview';

  return (
    <div className="league-page">
      <div className="league-detail-header">
        <button className="league-back-btn" onClick={() => navigate('/leagues')}>
          ← {t('nav.leagues')}
        </button>
        <div className="league-detail-titles">
          <h1 className="league-page-title">{league.name}</h1>
          <span className="league-mode-badge">{league.game_mode_display}</span>
        </div>
        {seasons.length > 0 && (
          <select
            className="league-season-select"
            value={seasonId || ''}
            onChange={(e) => setSeasonId(e.target.value)}
          >
            {seasons.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.status_display || s.status})
              </option>
            ))}
          </select>
        )}
      </div>

      {league.description && <p className="league-description">{league.description}</p>}

      {/* Admins get a separate tab for league administration; the schedule and
          standings stay on the public overview. */}
      {canManageLeagues && (
        <div className="league-tabs" role="tablist">
          <button
            className={`league-tab ${tab === 'overview' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            {t('leagues.tabOverview')}
          </button>
          <button
            className={`league-tab ${tab === 'admin' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('admin')}
          >
            {t('leagues.tabAdmin')}
          </button>
        </div>
      )}

      {tab === 'overview' ? (
        <>
          <JoinSeason
            league={league}
            season={selectedSeason}
            standings={standings}
            onJoined={refreshStandings}
          />

          <SeasonChampions medals={medals} seasonName={selectedSeason?.name || league.name} />

          {selectedSeason?.playoff_tournament_id && (
            <button
              className="league-playoff-link"
              onClick={() => navigate(`/tournaments/${selectedSeason.playoff_tournament_id}`)}
            >
              🏆 {t('leagues.viewPlayoffBracket')}
            </button>
          )}

          {divisions.length === 0 ? (
            <div className="league-empty">{t('leagues.noDivisions')}</div>
          ) : (
            <div className="division-grid">
              {divisions.map((div) => (
                <DivisionStandings
                  key={div.id}
                  division={div}
                  teamBased={Boolean(league.is_team_based)}
                  onRow={openRow}
                />
              ))}
            </div>
          )}

          {seasonId && <SeasonPlayerStats seasonId={seasonId} />}

          {seasonId && <SeasonFixtures seasonId={seasonId} />}

          <LeagueAllTimeStats leagueId={league.id} />

          <LeagueHallOfFame leagueId={league.id} />
        </>
      ) : (
        <LeagueAdminPanel
          league={league}
          seasons={seasons}
          seasonId={seasonId}
          standings={standings}
          onSeasonsChanged={refreshSeasons}
          onStandingsChanged={refreshStandings}
          onLeagueChanged={refreshLeague}
        />
      )}
    </div>
  );
}
